import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Note } from '../abf/onboarding/review/contractReviewStyles';
import { type SectionDiff, type TemplateDiff, getDiff, templateErrorMessage } from './documentTemplatesApi';

const Block = styled.section`
  border: 1px solid #29374b; border-radius: 10px; background: #0c1524; margin-bottom: 14px; padding: 14px 16px;
  h3 { font-size: 14px; margin-bottom: 8px; }
  li { list-style: none; margin: 6px 0; font-size: 13px; color: #c5d4e6; }
  ul { padding: 0; margin: 0; }
  code { color: #e5edf7; }
  .added { color: #6ee7b7; }
  .removed { color: #fca5a5; }
  .property { color: #91a5bd; }
`;

const show = (value: unknown) => {
  if (value === null || value === undefined) return 'nothing';
  if (typeof value === 'string') return `"${value}"`;
  return JSON.stringify(value);
};

const PROPERTY_WORDS: Record<string, string> = {
  label: 'label', instruction: 'instruction', required: 'must be stated', booking_key: 'books into',
  citation: 'citation', review_group: 'review group', type: 'type', validation: 'rules', values: 'allowed values',
  item_fields: 'row fields', item_type: 'list of', topic: 'long-agreement topic',
};

const section = (title: string, diff: SectionDiff) => {
  if (!diff.added.length && !diff.removed.length && !diff.changed.length) return null;
  return (
    <Block key={title}>
      <h3>{title}</h3>
      <ul>
        {diff.added.map(entry => <li key={`a${entry.path}`}><span className="added">Added</span> {entry.label} <code>{entry.path}</code></li>)}
        {diff.removed.map(entry => <li key={`r${entry.path}`}><span className="removed">Removed</span> {entry.label} <code>{entry.path}</code></li>)}
        {diff.changed.map(change => (
          <li key={`c${change.path}`}>
            <code>{change.path}</code>
            <ul>
              {change.changes.map(c => (
                <li key={c.property} style={{ marginLeft: 16 }}>
                  <span className="property">{PROPERTY_WORDS[c.property] || c.property}</span>: {show(c.from)} → {show(c.to)}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </Block>
  );
};

/** What this version changes against the version in use, booking keys first. */
const TemplateDiffPanel: React.FC<{ versionId: number; refreshKey: string }> = ({ versionId, refreshKey }) => {
  const [diff, setDiff] = useState<TemplateDiff | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    getDiff(versionId)
      .then(result => { if (alive) { setDiff(result); setError(null); } })
      .catch(failure => { if (alive) setError(templateErrorMessage(failure, 'The changes could not be loaded')); });
    return () => { alive = false; };
  }, [versionId, refreshKey]);

  if (error) return <Note role="alert">{error}</Note>;
  if (!diff) return <Note>Loading…</Note>;
  if (!diff.from) return <Note>No version of this type is in use yet, so everything in this version is new.</Note>;
  if (diff.to.id === diff.from.id) return <Note>This is the version in use.</Note>;
  if (diff.unchanged && !Object.keys(diff.header).length) {
    return <Note>Saved changes: none yet. This version is the same as version {diff.from.version}, which is in use.</Note>;
  }
  return (
    <div>
      <Note style={{ marginBottom: 14 }}>Against version {diff.from.version}, in use. Only saved changes are shown.</Note>
      {(diff.bookingKeys.added.length > 0 || diff.bookingKeys.removed.length > 0) && (
        <Block>
          <h3>What books</h3>
          <ul>
            {diff.bookingKeys.added.map(key => <li key={`a${key}`}><span className="added">Now booked</span> <code>{key}</code></li>)}
            {diff.bookingKeys.removed.map(key => <li key={`r${key}`}><span className="removed">No longer booked</span> <code>{key}</code></li>)}
          </ul>
        </Block>
      )}
      {Object.keys(diff.header).length > 0 && (
        <Block>
          <h3>Name and upload hints</h3>
          <ul>
            {Object.entries(diff.header).map(([key, change]) => (
              <li key={key}><span className="property">{key === 'filenameHints' ? 'filename hints' : key}</span>: {show(change.from)} → {show(change.to)}</li>
            ))}
          </ul>
        </Block>
      )}
      {section('Fields', diff.fields)}
      {section('Lists', diff.arrays)}
      {section('Groups', diff.objects)}
      {(diff.crossFieldChecks.added.length > 0 || diff.crossFieldChecks.removed.length > 0) && (
        <Block>
          <h3>Checks across fields</h3>
          <ul>
            {diff.crossFieldChecks.added.map((c, i) => <li key={`a${i}`}><span className="added">Added</span> {c.description || c.rule}</li>)}
            {diff.crossFieldChecks.removed.map((c, i) => <li key={`r${i}`}><span className="removed">Removed</span> {c.description || c.rule}</li>)}
          </ul>
        </Block>
      )}
    </div>
  );
};

export default TemplateDiffPanel;
